import type { ReactNode } from "react";
import { BluebirdLayout } from "@/components/bluebird/BluebirdLayout";
import { BreadcrumbJsonLd } from "@/components/shared/json-ld";
import { SlimFooter, SubHero, SubMasthead } from "@/components/bluebird/Chrome";
import { H2 } from "@/components/bluebird/primitives";
import { CalEmbed } from "@/components/bluebird/CalEmbed";
import { HeadlessBookingFlow } from "@/components/booking/HeadlessBookingFlow";
import { ContactWidget } from "@/components/shared/ContactWidget";
import { GoogleRatingBadge } from "@/components/bluebird/GoogleReviews";
import { color, font, maxWidth, radius, space, type } from "@/lib/design-tokens";
import { contact as copy } from "@/lib/copy/contact";
import { shop } from "@/lib/copy/shop";

/**
 * `/book` — a page with the live test-ride scheduler.
 *
 * Cal.com owns slot selection and confirmation. On this page the shared
 * contact widget sits inside the appointments card so it cannot cover booking
 * controls; every other Bluebird page keeps the fixed launcher.
 */
interface BookProps {
  headless_booking: { enabled: boolean };
  booking_today: string;
  booking_query_end: string;
  rider_limits: { max_adults: number; max_minors_per_adult: number };
  bike_brands: Array<{ slug: string; label: string }>;
  preselected_bike_brand?: string | null;
}

function Book({ headless_booking, booking_today, booking_query_end, rider_limits, bike_brands, preselected_bike_brand }: BookProps) {
  const bookingFacts = headless_booking.enabled ? copy.book.facts : copy.book.fallbackFacts;
  return (
    <>
      {/* Structured data only — renders no visible markup, so it cannot move a
          pixel in script/design_diff.mjs. Kept from the previous design because
          breadcrumbs are what let a search result show the path to the page. */}
      <BreadcrumbJsonLd items={[{ name: "Test rides", href: "/book" }]} />

      <SubMasthead
        current="/book"
        photo="hero-family"
        photoAlt="A family riding together on an Urban Arrow cargo bike"
        focalPosition="center 42%"
      >
        <SubHero
          line1={copy.book.line1}
          line2={copy.book.line2}
          intro={copy.book.intro}
          introMaxWidth="620px"
        />
      </SubMasthead>

      {/* The <main> landmark. A screen-reader user's "skip to content" jump
          lands here, and without it the only landmarks on the page are a banner
          and a footer with everything else unlabelled. <main> is display:block
          with no margin, so it wraps the same boxes at the same positions —
          verified by script/design_diff.mjs, which still reports 0%. */}
      <main>

        <section
          data-section="schedule"
          data-booking-mode={headless_booking.enabled ? "headless" : "iframe"}
          style={{
            maxWidth,
            margin: "0 auto",
            padding: `${space.sectionY} ${space.gutter}`,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(min(360px,100%),1fr))",
            gap: "clamp(32px,5vw,72px)",
            alignItems: "start",
          }}
        >
        <div data-section="facts">
          <H2 data-reveal>{copy.book.factsHeading}</H2>
          <ul style={{ margin: "20px 0 0", padding: 0, listStyle: "none", display: "grid", gap: "14px" }}>
            {bookingFacts.map((fact) => (
              <li
                key={fact}
                style={{ display: "flex", gap: "12px", fontSize: "16.5px", lineHeight: 1.55, color: color.inkMuted }}
              >
                <span
                  style={{
                    width: "22px",
                    height: "22px",
                    borderRadius: "50%",
                    background: color.blue,
                    color: color.gold,
                    fontSize: "12px",
                    fontWeight: 800,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "none",
                    marginTop: "2px",
                  }}
                >
                  ✓
                </span>
                {fact}
              </li>
            ))}
          </ul>
          <div style={{ marginTop: "24px" }}>
            <GoogleRatingBadge variant="light" />
          </div>
          <div
            data-book-recommendation=""
            style={{
              background: color.gold,
              borderRadius: radius.panel,
              padding: "clamp(24px,3vw,36px)",
              marginTop: "28px",
            }}
          >
            <div
              style={{
                fontFamily: font.mono,
                fontSize: "12px",
                letterSpacing: ".14em",
                textTransform: "uppercase",
                color: color.onGold,
              }}
            >
              Read this first
            </div>
            <H2 style={{ fontSize: type.h3, marginTop: "12px", color: color.ink }}>
              {copy.book.noBookingHeading}
            </H2>
            <p style={{ margin: "12px 0 0", fontSize: "16px", lineHeight: 1.55, color: color.onGold }}>
              {copy.book.noBookingBody}
            </p>
            <div
              style={{
                marginTop: "18px",
                fontSize: "16px",
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              <a href={shop.phoneHref} className="bb-underline-grow" style={{ fontWeight: 700 }}>
                {shop.phone}
              </a>
              <ContactWidget inline />
            </div>
          </div>
        </div>
        <div>
          <H2 data-reveal style={{ fontSize: type.h3, margin: "0 0 6px" }}>
            {copy.book.scheduleHeading}
          </H2>
          <p style={{ margin: "0 0 20px", fontSize: "16px", lineHeight: 1.55, color: color.inkMuted }}>
            {headless_booking.enabled ? copy.book.scheduleBlurb : copy.book.fallbackScheduleBlurb}
          </p>
          {headless_booking.enabled ? (
            <HeadlessBookingFlow
              bikeBrands={bike_brands}
              preselectedBikeBrand={preselected_bike_brand}
              startDate={booking_today}
              endDate={booking_query_end}
              riderLimits={rider_limits}
            />
          ) : (
            <CalEmbed />
          )}
        </div>
        </section>

      </main>

      <SlimFooter borderTop />
    </>
  );
}

Book.layout = (page: ReactNode) => <BluebirdLayout>{page}</BluebirdLayout>;

export default Book;
